<template>
    <div class="faqs">
      <h3>Prior Knowledge</h3>
      <p>Please answer the questions below honestly. If you have not worked in the industry before, it is likely that you will have little or no knowledge of any of the below. It is important that you are honest about this lack of knowledge so that we can best tailor your training plan to your current experience, knowledge and learning needs. Thank you.</p>
      <!-- Previous Experience -->
      <b-form-group label="Have you worked previously as a Sales Consultant for a Travel Agency?">
        <div class="radio radio-custom">
          <input id="previousSalesConsultantYes"
            name="previousSalesConsultant"
            v-model="previousSalesConsultant"
            type="radio"
            value="yes"
            @change="handleInput('previousSalesConsultant', previousSalesConsultant)"
          >
          <label for="previousSalesConsultantYes">Yes</label>
        </div>
        <div class="radio radio-custom">
          <input id="previousSalesConsultantNo"
            name="previousSalesConsultant"
            v-model="previousSalesConsultant"
            type="radio"
            value="no"
            @change="handleInput('previousSalesConsultant', previousSalesConsultant)"
          >
          <label for="previousSalesConsultantNo">No</label>
        </div>
      </b-form-group>
      <!-- Agency Name -->
      <b-form-group
        v-if="previousSalesConsultant === 'yes'"
        class="mt-4"
        label="What is the name of the agency?"
      >
        <b-form-input
          :class="{
            'is-valid': !$v.form.agencyName.$error && form.agencyName,
            'is-invalid': $v.form.agencyName.$error
          }"
          :value="form.agencyName"
          @input="handleInput('agencyName', $event)"
        >
        </b-form-input>
      </b-form-group>
      <!-- Duration at agency -->
      <b-form-group
        v-if="previousSalesConsultant === 'yes'"
        class="mt-4"
        label="How long did you work there?"
      >
        <b-form-input
          :class="{
            'is-valid': !$v.form.howLong.$error && form.howLong,
            'is-invalid': $v.form.howLong.$error
          }"
          :value="form.howLong"
          @input="handleInput('howLong', $event)"
        >
        </b-form-input>
      </b-form-group>
      <!-- Qualifications -->
      <b-form-group
        label="List any qualifications you have that relate to the travel industry"
      >
        <b-form-textarea
          :value="form.travelQualifications"
          @input="handleInput('travelQualifications', $event)"
          :class="{
            'is-valid': !$v.form.travelQualifications.$error && form.travelQualifications,
            'is-invalid': $v.form.travelQualifications.$error
          }"
        ></b-form-textarea>
      </b-form-group>
      <!-- Formal Training -->
      <b-form-group label="Have you ever had any formal training in sales techniques?">
        <div class="radio radio-custom">
          <input id="salesTrainingYes"
            name="salesTraining"
            v-model="salesTraining"
            type="radio"
            value="yes"
            @change="handleInput('salesTraining', salesTraining)"
          >
          <label for="salesTrainingYes">Yes</label>
        </div>
        <div class="radio radio-custom">
          <input id="salesTrainingNo"
            name="salesTraining"
            v-model="salesTraining"
            type="radio"
            value="no"
            @change="handleInput('salesTraining', salesTraining)"
          >
          <label for="salesTrainingNo">No</label>
        </div>
      </b-form-group>
      <!-- Training details -->
      <b-form-group
        v-if="salesTraining === 'yes'"
        label="Please detail any training you have had"
      >
        <b-form-textarea
          :value="form.salesTrainingDetails"
          @input="handleInput('salesTrainingDetails', $event)"
          :class="{
            'is-valid': !$v.form.salesTrainingDetails.$error && form.salesTrainingDetails,
            'is-invalid': $v.form.salesTrainingDetails.$error
          }"
        ></b-form-textarea>
      </b-form-group>
      <!-- Package Travel Regs -->
      <b-form-group label="How would you judge your familiarity with the package Travel Regulations?">
        <div class="radio radio-custom">
          <input id="packageTravelRegulationsA"
            name="packageTravelRegulations"
            v-model="packageTravelRegulations"
            type="radio"
            value="A: I am very familiar with the PTRs and would be confident advising clients"
            @change="handleInput('packageTravelRegulations', packageTravelRegulations)"
          >
          <label for="packageTravelRegulationsA">I am very familiar with the PTRs and would be confident advising clients</label>
        </div>
        <div class="radio radio-custom">
          <input id="packageTravelRegulationsB"
            name="packageTravelRegulations"
            v-model="packageTravelRegulations"
            type="radio"
            value="B: I am aware they exist but do not know the details"
            @change="handleInput('packageTravelRegulations', packageTravelRegulations)"
          >
          <label for="packageTravelRegulationsB">I am aware they exist but do not know the details</label>
        </div>
        <div class="radio radio-custom">
          <input id="packageTravelRegulationsC"
            name="packageTravelRegulations"
            v-model="packageTravelRegulations"
            type="radio"
            value="C: I have never heard of them"
            @change="handleInput('packageTravelRegulations', packageTravelRegulations)"
          >
          <label for="packageTravelRegulationsC">I have never heard of them</label>
        </div>
      </b-form-group>
      <!-- GDS -->
      <b-form-group
        label="Do you have experience of booking flights via a GDS (Global Distribution System) such as Galileo?"
      >
        <b-form-textarea
          :value="form.flightGDS"
          @input="handleInput('flightGDS', $event)"
          :class="{
            'is-valid': !$v.form.flightGDS.$error && form.flightGDS,
            'is-invalid': $v.form.flightGDS.$error
          }"
        ></b-form-textarea>
      </b-form-group>
      <p>Do you have experience of advising customers on the following?</p>
      <!-- Passport and visa experience -->
      <b-form-group label="Passport and visa requirements">
        <div class="radio radio-custom">
          <input id="passportAndVisaExperienceA"
            name="passportAndVisaExperience"
            v-model="passportAndVisaExperience"
            type="radio"
            value="A: I have no experience in this area"
            @change="handleInput('passportAndVisaExperience', passportAndVisaExperience)"
          >
          <label for="passportAndVisaExperienceA">I have no experience in this area</label>
        </div>
        <div class="radio radio-custom">
          <input id="passportAndVisaExperienceB"
            name="passportAndVisaExperience"
            v-model="passportAndVisaExperience"
            type="radio"
            value="B: I have some experience in this area, but will require further training"
            @change="handleInput('passportAndVisaExperience', passportAndVisaExperience)"
          >
          <label for="passportAndVisaExperienceB">I have some experience in this area, but will require further training</label>
        </div>
        <div class="radio radio-custom">
          <input id="passportAndVisaExperienceC"
            name="passportAndVisaExperience"
            v-model="passportAndVisaExperience"
            type="radio"
            value="C: I am confident in this area"
            @change="handleInput('passportAndVisaExperience', passportAndVisaExperience)"
          >
          <label for="passportAndVisaExperienceC">I am confident in this area</label>
        </div>
      </b-form-group>
      <!-- FCDO Advice -->
      <b-form-group label="FCDO Advice">
        <div class="radio radio-custom">
          <input id="FCDOAdviceExperienceA"
            name="FCDOAdviceExperience"
            v-model="FCDOAdviceExperience"
            type="radio"
            value="A: I have no experience in this area"
            @change="handleInput('FCDOAdviceExperience', FCDOAdviceExperience)"
          >
          <label for="FCDOAdviceExperienceA">I have no experience in this area</label>
        </div>
        <div class="radio radio-custom">
          <input id="FCDOAdviceExperienceB"
            name="FCDOAdviceExperience"
            v-model="FCDOAdviceExperience"
            type="radio"
            value="B: I have some experience in this area, but will require further training"
            @change="handleInput('FCDOAdviceExperience', FCDOAdviceExperience)"
          >
          <label for="FCDOAdviceExperienceB">I have some experience in this area, but will require further training</label>
        </div>
        <div class="radio radio-custom">
          <input id="FCDOAdviceExperienceC"
            name="FCDOAdviceExperience"
            v-model="FCDOAdviceExperience"
            type="radio"
            value="C: I am confident in this area"
            @change="handleInput('FCDOAdviceExperience', FCDOAdviceExperience)"
          >
          <label for="FCDOAdviceExperienceC">I am confident in this area</label>
        </div>
      </b-form-group>
      <!-- Local Climate -->
      <b-form-group label="Local Climate Advice">
        <div class="radio radio-custom">
          <input id="localClimateExperienceA"
            name="localClimateExperience"
            v-model="localClimateExperience"
            type="radio"
            value="A: I have no experience in this area"
            @change="handleInput('localClimateExperience', localClimateExperience)"
          >
          <label for="localClimateExperienceA">I have no experience in this area</label>
        </div>
        <div class="radio radio-custom">
          <input id="localClimateExperienceB"
            name="localClimateExperience"
            v-model="localClimateExperience"
            type="radio"
            value="B: I have some experience in this area, but will require further training"
            @change="handleInput('localClimateExperience', localClimateExperience)"
          >
          <label for="localClimateExperienceB">I have some experience in this area, but will require further training</label>
        </div>
        <div class="radio radio-custom">
          <input id="localClimateExperienceC"
            name="localClimateExperience"
            v-model="localClimateExperience"
            type="radio"
            value="C: I am confident in this area"
            @change="handleInput('localClimateExperience', localClimateExperience)"
          >
          <label for="localClimateExperienceC">I am confident in this area</label>
        </div>
      </b-form-group>
      <p>How would you judge your knowledge on the following key destinations, in terms of knowing the main sights and attractions, how best to travel around, the quality and variety of accommodation on offer, touring options, best times of year to travel and understanding approximate budget required?</p>
      <!-- California experience -->
      <b-form-group label="USA - California">
        <div class="radio radio-custom">
          <input id="californiaKnowledgeA"
            name="californiaKnowledge"
            v-model="californiaKnowledge"
            type="radio"
            value="A: I currently know very little about this area"
            @change="handleInput('californiaKnowledge', californiaKnowledge)"
          >
          <label for="californiaKnowledgeA">I currently know very little about this area</label>
        </div>
        <div class="radio radio-custom">
          <input id="californiaKnowledgeB"
            name="californiaKnowledge"
            v-model="californiaKnowledge"
            type="radio"
            value="B: I have some knowledge about this area"
            @change="handleInput('californiaKnowledge', californiaKnowledge)"
          >
          <label for="californiaKnowledgeB">I have some knowledge about this area</label>
        </div>
        <div class="radio radio-custom">
          <input id="californiaKnowledgeC"
            name="californiaKnowledge"
            v-model="californiaKnowledge"
            type="radio"
            value="C: I already have a strong knowledge of this area"
            @change="handleInput('californiaKnowledge', californiaKnowledge)"
          >
          <label for="californiaKnowledgeC">I already have a strong knowledge of this area</label>
        </div>
      </b-form-group>
      <!-- Florida experience -->
      <b-form-group label="USA - Florida">
        <div class="radio radio-custom">
          <input id="floridaKnowledgeA"
            name="floridaKnowledge"
            v-model="floridaKnowledge"
            type="radio"
            value="A: I currently know very little about this area"
            @change="handleInput('floridaKnowledge', floridaKnowledge)"
          >
          <label for="floridaKnowledgeA">I currently know very little about this area</label>
        </div>
        <div class="radio radio-custom">
          <input id="floridaKnowledgeB"
            name="floridaKnowledge"
            v-model="floridaKnowledge"
            type="radio"
            value="B: I have some knowledge about this area"
            @change="handleInput('floridaKnowledge', floridaKnowledge)"
          >
          <label for="floridaKnowledgeB">I have some knowledge about this area</label>
        </div>
        <div class="radio radio-custom">
          <input id="floridaKnowledgeC"
            name="floridaKnowledge"
            v-model="floridaKnowledge"
            type="radio"
            value="C: I already have a strong knowledge of this area"
            @change="handleInput('floridaKnowledge', floridaKnowledge)"
          >
          <label for="floridaKnowledgeC">I already have a strong knowledge of this area</label>
        </div>
      </b-form-group>
       <!-- Western Canada -->
       <b-form-group label="Western Canada">
        <div class="radio radio-custom">
          <input id="westCanadaKnowledgeA"
            name="westCanadaKnowledge"
            v-model="westCanadaKnowledge"
            type="radio"
            value="A: I currently know very little about this area"
            @change="handleInput('westCanadaKnowledge', westCanadaKnowledge)"
          >
          <label for="westCanadaKnowledgeA">I currently know very little about this area</label>
        </div>
        <div class="radio radio-custom">
          <input id="westCanadaKnowledgeB"
            name="westCanadaKnowledge"
            v-model="westCanadaKnowledge"
            type="radio"
            value="B: I have some knowledge about this area"
            @change="handleInput('westCanadaKnowledge', westCanadaKnowledge)"
          >
          <label for="westCanadaKnowledgeB">I have some knowledge about this area</label>
        </div>
        <div class="radio radio-custom">
          <input id="westCanadaKnowledgeC"
            name="westCanadaKnowledge"
            v-model="westCanadaKnowledge"
            type="radio"
            value="C: I already have a strong knowledge of this area"
            @change="handleInput('westCanadaKnowledge', westCanadaKnowledge)"
          >
          <label for="westCanadaKnowledgeC">I already have a strong knowledge of this area</label>
        </div>
      </b-form-group>
       <!-- East Coast Australia -->
       <b-form-group label="East Coast Australia">
        <div class="radio radio-custom">
          <input id="eastCoastAustraliaKnowledgeA"
            name="eastCoastAustraliaKnowledge"
            v-model="eastCoastAustraliaKnowledge"
            type="radio"
            value="A: I currently know very little about this area"
            @change="handleInput('eastCoastAustraliaKnowledge', eastCoastAustraliaKnowledge)"
          >
          <label for="eastCoastAustraliaKnowledgeA">I currently know very little about this area</label>
        </div>
        <div class="radio radio-custom">
          <input id="eastCoastAustraliaKnowledgeB"
            name="eastCoastAustraliaKnowledge"
            v-model="eastCoastAustraliaKnowledge"
            type="radio"
            value="B: I have some knowledge about this area"
            @change="handleInput('eastCoastAustraliaKnowledge', eastCoastAustraliaKnowledge)"
          >
          <label for="eastCoastAustraliaKnowledgeB">I have some knowledge about this area</label>
        </div>
        <div class="radio radio-custom">
          <input id="eastCoastAustraliaKnowledgeC"
            name="eastCoastAustraliaKnowledge"
            v-model="eastCoastAustraliaKnowledge"
            type="radio"
            value="C: I already have a strong knowledge of this area"
            @change="handleInput('eastCoastAustraliaKnowledge', eastCoastAustraliaKnowledge)"
          >
          <label for="eastCoastAustraliaKnowledgeC">I already have a strong knowledge of this area</label>
        </div>
      </b-form-group>
      <!-- New Zealand -->
      <b-form-group label="New Zealand">
        <div class="radio radio-custom">
          <input id="newZealandKnowledgeA"
            name="newZealandKnowledge"
            v-model="newZealandKnowledge"
            type="radio"
            value="A: I currently know very little about this area"
            @change="handleInput('newZealandKnowledge', newZealandKnowledge)"
          >
          <label for="newZealandKnowledgeA">I currently know very little about this area</label>
        </div>
        <div class="radio radio-custom">
          <input id="newZealandKnowledgeB"
            name="newZealandKnowledge"
            v-model="newZealandKnowledge"
            type="radio"
            value="B: I have some knowledge about this area"
            @change="handleInput('newZealandKnowledge', newZealandKnowledge)"
          >
          <label for="newZealandKnowledgeB">I have some knowledge about this area</label>
        </div>
        <div class="radio radio-custom">
          <input id="newZealandKnowledgeC"
            name="newZealandKnowledge"
            v-model="newZealandKnowledge"
            type="radio"
            value="C: I already have a strong knowledge of this area"
            @change="handleInput('newZealandKnowledge', newZealandKnowledge)"
          >
          <label for="newZealandKnowledgeC">I already have a strong knowledge of this area</label>
        </div>
      </b-form-group>
      <!-- South Africa -->
      <b-form-group label="South Africa">
        <div class="radio radio-custom">
          <input id="southAfricaKnowledgeA"
            name="southAfricaKnowledge"
            v-model="southAfricaKnowledge"
            type="radio"
            value="A: I currently know very little about this area"
            @change="handleInput('southAfricaKnowledge', southAfricaKnowledge)"
          >
          <label for="southAfricaKnowledgeA">I currently know very little about this area</label>
        </div>
        <div class="radio radio-custom">
          <input id="southAfricaKnowledgeB"
            name="southAfricaKnowledge"
            v-model="southAfricaKnowledge"
            type="radio"
            value="B: I have some knowledge about this area"
            @change="handleInput('southAfricaKnowledge', southAfricaKnowledge)"
          >
          <label for="southAfricaKnowledgeB">I have some knowledge about this area</label>
        </div>
        <div class="radio radio-custom">
          <input id="southAfricaKnowledgeC"
            name="southAfricaKnowledge"
            v-model="southAfricaKnowledge"
            type="radio"
            value="C: I already have a strong knowledge of this area"
            @change="handleInput('southAfricaKnowledge', southAfricaKnowledge)"
          >
          <label for="southAfricaKnowledgeC">I already have a strong knowledge of this area</label>
        </div>
      </b-form-group>
      <!-- Thailand -->
      <b-form-group label="Thailand">
        <div class="radio radio-custom">
          <input id="thailandKnowledgeA"
            name="thailandKnowledge"
            v-model="thailandKnowledge"
            type="radio"
            value="A: I currently know very little about this area"
            @change="handleInput('thailandKnowledge', thailandKnowledge)"
          >
          <label for="thailandKnowledgeA">I currently know very little about this area</label>
        </div>
        <div class="radio radio-custom">
          <input id="thailandKnowledgeB"
            name="thailandKnowledge"
            v-model="thailandKnowledge"
            type="radio"
            value="B: I have some knowledge about this area"
            @change="handleInput('thailandKnowledge', thailandKnowledge)"
          >
          <label for="thailandKnowledgeB">I have some knowledge about this area</label>
        </div>
        <div class="radio radio-custom">
          <input id="thailandKnowledgeC"
            name="thailandKnowledge"
            v-model="thailandKnowledge"
            type="radio"
            value="C: I already have a strong knowledge of this area"
            @change="handleInput('thailandKnowledge', thailandKnowledge)"
          >
          <label for="thailandKnowledgeC">I already have a strong knowledge of this area</label>
        </div>
      </b-form-group>
      <!-- Italy -->
      <b-form-group label="Italy">
        <div class="radio radio-custom">
          <input id="italyKnowledgeA"
            name="italyKnowledge"
            v-model="italyKnowledge"
            type="radio"
            value="A: I currently know very little about this area"
            @change="handleInput('italyKnowledge', italyKnowledge)"
          >
          <label for="italyKnowledgeA">I currently know very little about this area</label>
        </div>
        <div class="radio radio-custom">
          <input id="italyKnowledgeB"
            name="italyKnowledge"
            v-model="italyKnowledge"
            type="radio"
            value="B: I have some knowledge about this area"
            @change="handleInput('italyKnowledge', italyKnowledge)"
          >
          <label for="italyKnowledgeB">I have some knowledge about this area</label>
        </div>
        <div class="radio radio-custom">
          <input id="italyKnowledgeC"
            name="italyKnowledge"
            v-model="italyKnowledge"
            type="radio"
            value="C: I already have a strong knowledge of this area"
            @change="handleInput('italyKnowledge', italyKnowledge)"
          >
          <label for="italyKnowledgeC">I already have a strong knowledge of this area</label>
        </div>
      </b-form-group>
       <!-- Spain -->
       <b-form-group label="Spain">
        <div class="radio radio-custom">
          <input id="spainKnowledgeA"
            name="spainKnowledge"
            v-model="spainKnowledge"
            type="radio"
            value="A: I currently know very little about this area"
            @change="handleInput('spainKnowledge', spainKnowledge)"
          >
          <label for="spainKnowledgeA">I currently know very little about this area</label>
        </div>
        <div class="radio radio-custom">
          <input id="spainKnowledgeB"
            name="spainKnowledge"
            v-model="spainKnowledge"
            type="radio"
            value="B: I have some knowledge about this area"
            @change="handleInput('spainKnowledge', spainKnowledge)"
          >
          <label for="spainKnowledgeB">I have some knowledge about this area</label>
        </div>
        <div class="radio radio-custom">
          <input id="spainKnowledgeC"
            name="spainKnowledge"
            v-model="spainKnowledge"
            type="radio"
            value="C: I already have a strong knowledge of this area"
            @change="handleInput('spainKnowledge', spainKnowledge)"
          >
          <label for="spainKnowledgeC">I already have a strong knowledge of this area</label>
        </div>
      </b-form-group>
      <!-- Vietnam -->
      <b-form-group label="Vietnam">
        <div class="radio radio-custom">
          <input id="vietnamKnowledgeA"
            name="vietnamKnowledge"
            v-model="vietnamKnowledge"
            type="radio"
            value="A: I currently know very little about this area"
            @change="handleInput('vietnamKnowledge', vietnamKnowledge)"
          >
          <label for="vietnamKnowledgeA">I currently know very little about this area</label>
        </div>
        <div class="radio radio-custom">
          <input id="vietnamKnowledgeB"
            name="vietnamKnowledge"
            v-model="vietnamKnowledge"
            type="radio"
            value="B: I have some knowledge about this area"
            @change="handleInput('vietnamKnowledge', vietnamKnowledge)"
          >
          <label for="vietnamKnowledgeB">I have some knowledge about this area</label>
        </div>
        <div class="radio radio-custom">
          <input id="vietnamKnowledgeC"
            name="vietnamKnowledge"
            v-model="vietnamKnowledge"
            type="radio"
            value="C: I already have a strong knowledge of this area"
            @change="handleInput('vietnamKnowledge', vietnamKnowledge)"
          >
          <label for="vietnamKnowledgeC">I already have a strong knowledge of this area</label>
        </div>
      </b-form-group>
      <!-- Other destinations -->
      <b-form-group
        label="Please list all destinations, if any, where you are already confident of your knowledge and do not feel you would gain from further learning"
      >
        <b-form-textarea
          :value="form.destinationKnowledge"
          @input="handleInput('destinationKnowledge', $event)"
          :class="{
            'is-valid': !$v.form.destinationKnowledge.$error && form.destinationKnowledge,
            'is-invalid': $v.form.destinationKnowledge.$error
          }"
        ></b-form-textarea>
      </b-form-group>
      <p>How confident do you feel in your knowledge of the following travel styles?</p>
      <!-- Fly drive -->
      <b-form-group label="Fly Drive Holidays">
        <div class="radio radio-custom">
          <input id="flyDriveKnowledgeA"
            name="flyDriveKnowledge"
            v-model="flyDriveKnowledge"
            type="radio"
            value="A: I currently know very little about this style of travel"
            @change="handleInput('flyDriveKnowledge', flyDriveKnowledge)"
          >
          <label for="flyDriveKnowledgeA">I currently know very little about this style of travel</label>
        </div>
        <div class="radio radio-custom">
          <input id="flyDriveKnowledgeB"
            name="flyDriveKnowledge"
            v-model="flyDriveKnowledge"
            type="radio"
            value="B: I have some knowledge about this style of travel"
            @change="handleInput('flyDriveKnowledge', flyDriveKnowledge)"
          >
          <label for="flyDriveKnowledgeB">I have some knowledge about this style of travel</label>
        </div>
        <div class="radio radio-custom">
          <input id="flyDriveKnowledgeC"
            name="flyDriveKnowledge"
            v-model="flyDriveKnowledge"
            type="radio"
            value="C: I already have a strong knowledge of this style of travel"
            @change="handleInput('flyDriveKnowledge', flyDriveKnowledge)"
          >
          <label for="flyDriveKnowledgeC">I already have a strong knowledge of this style of travel</label>
        </div>
      </b-form-group>
      <!-- Luxury Beach -->
      <b-form-group label="Luxury Beach Holidays">
        <div class="radio radio-custom">
          <input id="luxuryBeachKnowledgeA"
            name="luxuryBeachKnowledge"
            v-model="luxuryBeachKnowledge"
            type="radio"
            value="A: I currently know very little about this style of travel"
            @change="handleInput('luxuryBeachKnowledge', luxuryBeachKnowledge)"
          >
          <label for="luxuryBeachKnowledgeA">I currently know very little about this style of travel</label>
        </div>
        <div class="radio radio-custom">
          <input id="luxuryBeachKnowledgeB"
            name="luxuryBeachKnowledge"
            v-model="luxuryBeachKnowledge"
            type="radio"
            value="B: I have some knowledge about this style of travel"
            @change="handleInput('luxuryBeachKnowledge', luxuryBeachKnowledge)"
          >
          <label for="luxuryBeachKnowledgeB">I have some knowledge about this style of travel</label>
        </div>
        <div class="radio radio-custom">
          <input id="luxuryBeachKnowledgeC"
            name="luxuryBeachKnowledge"
            v-model="luxuryBeachKnowledge"
            type="radio"
            value="C: I already have a strong knowledge of this style of travel"
            @change="handleInput('luxuryBeachKnowledge', luxuryBeachKnowledge)"
          >
          <label for="luxuryBeachKnowledgeC">I already have a strong knowledge of this style of travel</label>
        </div>
      </b-form-group>
      <!-- Family -->
      <b-form-group label="Family Holidays">
        <div class="radio radio-custom">
          <input id="familyKKnowledgeA"
            name="familyKKnowledge"
            v-model="familyKKnowledge"
            type="radio"
            value="A: I currently know very little about this style of travel"
            @change="handleInput('familyKKnowledge', familyKKnowledge)"
          >
          <label for="familyKKnowledgeA">I currently know very little about this style of travel</label>
        </div>
        <div class="radio radio-custom">
          <input id="familyKKnowledgeB"
            name="familyKKnowledge"
            v-model="familyKKnowledge"
            type="radio"
            value="B: I have some knowledge about this style of travel"
            @change="handleInput('familyKKnowledge', familyKKnowledge)"
          >
          <label for="familyKKnowledgeB">I have some knowledge about this style of travel</label>
        </div>
        <div class="radio radio-custom">
          <input id="familyKKnowledgeC"
            name="familyKKnowledge"
            v-model="familyKKnowledge"
            type="radio"
            value="C: I already have a strong knowledge of this style of travel"
            @change="handleInput('familyKKnowledge', familyKKnowledge)"
          >
          <label for="familyKKnowledgeC">I already have a strong knowledge of this style of travel</label>
        </div>
      </b-form-group>
      <!-- Bespoke -->
      <b-form-group label="Bespoke, Private Touring">
        <div class="radio radio-custom">
          <input id="bespokePrivateKnowledgeA"
            name="bespokePrivateKnowledge"
            v-model="bespokePrivateKnowledge"
            type="radio"
            value="A: I currently know very little about this style of travel"
            @change="handleInput('bespokePrivateKnowledge', bespokePrivateKnowledge)"
          >
          <label for="bespokePrivateKnowledgeA">I currently know very little about this style of travel</label>
        </div>
        <div class="radio radio-custom">
          <input id="bespokePrivateKnowledgeB"
            name="bespokePrivateKnowledge"
            v-model="bespokePrivateKnowledge"
            type="radio"
            value="B: I have some knowledge about this style of travel"
            @change="handleInput('bespokePrivateKnowledge', bespokePrivateKnowledge)"
          >
          <label for="bespokePrivateKnowledgeB">I have some knowledge about this style of travel</label>
        </div>
        <div class="radio radio-custom">
          <input id="bespokePrivateKnowledgeC"
            name="bespokePrivateKnowledge"
            v-model="bespokePrivateKnowledge"
            type="radio"
            value="C: I already have a strong knowledge of this style of travel"
            @change="handleInput('bespokePrivateKnowledge', bespokePrivateKnowledge)"
          >
          <label for="bespokePrivateKnowledgeC">I already have a strong knowledge of this style of travel</label>
        </div>
      </b-form-group>
      <!-- Escorted -->
      <b-form-group label="Escorted Coaching Touring">
        <div class="radio radio-custom">
          <input id="escortedCoachingKnowledgeA"
            name="escortedCoachingKnowledge"
            v-model="escortedCoachingKnowledge"
            type="radio"
            value="A: I currently know very little about this style of travel"
            @change="handleInput('escortedCoachingKnowledge', escortedCoachingKnowledge)"
          >
          <label for="escortedCoachingKnowledgeA">I currently know very little about this style of travel</label>
        </div>
        <div class="radio radio-custom">
          <input id="escortedCoachingKnowledgeB"
            name="escortedCoachingKnowledge"
            v-model="escortedCoachingKnowledge"
            type="radio"
            value="B: I have some knowledge about this style of travel"
            @change="handleInput('escortedCoachingKnowledge', escortedCoachingKnowledge)"
          >
          <label for="escortedCoachingKnowledgeB">I have some knowledge about this style of travel</label>
        </div>
        <div class="radio radio-custom">
          <input id="escortedCoachingKnowledgeC"
            name="escortedCoachingKnowledge"
            v-model="escortedCoachingKnowledge"
            type="radio"
            value="C: I already have a strong knowledge of this style of travel"
            @change="handleInput('escortedCoachingKnowledge', escortedCoachingKnowledge)"
          >
          <label for="escortedCoachingKnowledgeC">I already have a strong knowledge of this style of travel</label>
        </div>
      </b-form-group>
      <!-- Adventure -->
      <b-form-group label="Adventure Group Touring">
        <div class="radio radio-custom">
          <input id="adventureGroupKnowledgeA"
            name="adventureGroupKnowledge"
            v-model="adventureGroupKnowledge"
            type="radio"
            value="A: I currently know very little about this style of travel"
            @change="handleInput('adventureGroupKnowledge', adventureGroupKnowledge)"
          >
          <label for="adventureGroupKnowledgeA">I currently know very little about this style of travel</label>
        </div>
        <div class="radio radio-custom">
          <input id="adventureGroupKnowledgeB"
            name="adventureGroupKnowledge"
            v-model="adventureGroupKnowledge"
            type="radio"
            value="B: I have some knowledge about this style of travel"
            @change="handleInput('adventureGroupKnowledge', adventureGroupKnowledge)"
          >
          <label for="adventureGroupKnowledgeB">I have some knowledge about this style of travel</label>
        </div>
        <div class="radio radio-custom">
          <input id="adventureGroupKnowledgeC"
            name="adventureGroupKnowledge"
            v-model="adventureGroupKnowledge"
            type="radio"
            value="C: I already have a strong knowledge of this style of travel"
            @change="handleInput('adventureGroupKnowledge', adventureGroupKnowledge)"
          >
          <label for="adventureGroupKnowledgeC">I already have a strong knowledge of this style of travel</label>
        </div>
      </b-form-group>
      <!-- Cruises -->
      <b-form-group label="Cruises">
        <div class="radio radio-custom">
          <input id="cruiseKnowledgeA"
            name="cruiseKnowledge"
            v-model="cruiseKnowledge"
            type="radio"
            value="A: I currently know very little about this style of travel"
            @change="handleInput('cruiseKnowledge', cruiseKnowledge)"
          >
          <label for="cruiseKnowledgeA">I currently know very little about this style of travel</label>
        </div>
        <div class="radio radio-custom">
          <input id="cruiseKnowledgeB"
            name="cruiseKnowledge"
            v-model="cruiseKnowledge"
            type="radio"
            value="B: I have some knowledge about this style of travel"
            @change="handleInput('cruiseKnowledge', cruiseKnowledge)"
          >
          <label for="cruiseKnowledgeB">I have some knowledge about this style of travel</label>
        </div>
        <div class="radio radio-custom">
          <input id="cruiseKnowledgeC"
            name="cruiseKnowledge"
            v-model="cruiseKnowledge"
            type="radio"
            value="C: I already have a strong knowledge of this style of travel"
            @change="handleInput('cruiseKnowledge', cruiseKnowledge)"
          >
          <label for="cruiseKnowledgeC">I already have a strong knowledge of this style of travel</label>
        </div>
      </b-form-group>
       <!-- GAP -->
       <b-form-group label="GAP year back-packing">
        <div class="radio radio-custom">
          <input id="GAPYearKnowledgeA"
            name="GAPYearKnowledge"
            v-model="GAPYearKnowledge"
            type="radio"
            value="A: I currently know very little about this style of travel"
            @change="handleInput('GAPYearKnowledge', GAPYearKnowledge)"
          >
          <label for="GAPYearKnowledgeA">I currently know very little about this style of travel</label>
        </div>
        <div class="radio radio-custom">
          <input id="GAPYearKnowledgeB"
            name="GAPYearKnowledge"
            v-model="GAPYearKnowledge"
            type="radio"
            value="B: I have some knowledge about this style of travel"
            @change="handleInput('GAPYearKnowledge', GAPYearKnowledge)"
          >
          <label for="GAPYearKnowledgeB">I have some knowledge about this style of travel</label>
        </div>
        <div class="radio radio-custom">
          <input id="GAPYearKnowledgeC"
            name="GAPYearKnowledge"
            v-model="GAPYearKnowledge"
            type="radio"
            value="C: I already have a strong knowledge of this style of travel"
            @change="handleInput('GAPYearKnowledge', GAPYearKnowledge)"
          >
          <label for="GAPYearKnowledgeC">I already have a strong knowledge of this style of travel</label>
        </div>
      </b-form-group>
       <!-- Buisness Class Flights -->
       <b-form-group label="Business Class Flights">
        <div class="radio radio-custom">
          <input id="businessClassFlightsKnowledgeA"
            name="businessClassFlightsKnowledge"
            v-model="businessClassFlightsKnowledge"
            type="radio"
            value="A: I currently know very little about this style of travel"
            @change="handleInput('businessClassFlightsKnowledge', businessClassFlightsKnowledge)"
          >
          <label for="businessClassFlightsKnowledgeA">I currently know very little about this style of travel</label>
        </div>
        <div class="radio radio-custom">
          <input id="businessClassFlightsKnowledgeB"
            name="businessClassFlightsKnowledge"
            v-model="businessClassFlightsKnowledge"
            type="radio"
            value="B: I have some knowledge about this style of travel"
            @change="handleInput('businessClassFlightsKnowledge', businessClassFlightsKnowledge)"
          >
          <label for="businessClassFlightsKnowledgeB">I have some knowledge about this style of travel</label>
        </div>
        <div class="radio radio-custom">
          <input id="businessClassFlightsKnowledgeC"
            name="businessClassFlightsKnowledge"
            v-model="businessClassFlightsKnowledge"
            type="radio"
            value="C: I already have a strong knowledge of this style of travel"
            @change="handleInput('businessClassFlightsKnowledge', businessClassFlightsKnowledge)"
          >
          <label for="businessClassFlightsKnowledgeC">I already have a strong knowledge of this style of travel</label>
        </div>
      </b-form-group>
      <!-- Rail -->
      <b-form-group label="Rail Travel & Tours">
        <div class="radio radio-custom">
          <input id="railKnowledgeA"
            name="railKnowledge"
            v-model="railKnowledge"
            type="radio"
            value="A: I currently know very little about this style of travel"
            @change="handleInput('railKnowledge', railKnowledge)"
          >
          <label for="railKnowledgeA">I currently know very little about this style of travel</label>
        </div>
        <div class="radio radio-custom">
          <input id="railKnowledgeB"
            name="railKnowledge"
            v-model="railKnowledge"
            type="radio"
            value="B: I have some knowledge about this style of travel"
            @change="handleInput('railKnowledge', railKnowledge)"
          >
          <label for="railKnowledgeB">I have some knowledge about this style of travel</label>
        </div>
        <div class="radio radio-custom">
          <input id="railKnowledgeC"
            name="railKnowledge"
            v-model="railKnowledge"
            type="radio"
            value="C: I already have a strong knowledge of this style of travel"
            @change="handleInput('railKnowledge', railKnowledge)"
          >
          <label for="railKnowledgeC">I already have a strong knowledge of this style of travel</label>
        </div>
      </b-form-group>
       <!-- Ski -->
       <b-form-group label="Ski Holidays">
        <div class="radio radio-custom">
          <input id="skiKnowledgeA"
            name="skiKnowledge"
            v-model="skiKnowledge"
            type="radio"
            value="A: I currently know very little about this style of travel"
            @change="handleInput('skiKnowledge', skiKnowledge)"
          >
          <label for="skiKnowledgeA">I currently know very little about this style of travel</label>
        </div>
        <div class="radio radio-custom">
          <input id="skiKnowledgeB"
            name="skiKnowledge"
            v-model="skiKnowledge"
            type="radio"
            value="B: I have some knowledge about this style of travel"
            @change="handleInput('skiKnowledge', skiKnowledge)"
          >
          <label for="skiKnowledgeB">I have some knowledge about this style of travel</label>
        </div>
        <div class="radio radio-custom">
          <input id="skiKnowledgeC"
            name="skiKnowledge"
            v-model="skiKnowledge"
            type="radio"
            value="C: I already have a strong knowledge of this style of travel"
            @change="handleInput('skiKnowledge', skiKnowledge)"
          >
          <label for="skiKnowledgeC">I already have a strong knowledge of this style of travel</label>
        </div>
      </b-form-group>
    </div>
  </template>
  
  <script>
  import { mapGetters, mapState, mapActions } from 'vuex'
  import { updateForm } from '../../helpers/index'
  import { required, requiredIf } from 'vuelidate/lib/validators'
  import { uploadEnglishCertificate, uploadMathsCertificate } from '../../services/api'
  
  export default {
    computed: {
      ...mapState({form: state => state.authStore.recruit.onboarding.onboardingForms.priorKnowledge}),
      ...mapGetters({
        getRecruit: 'authStore/getRecruit',
        getToken: 'authStore/getToken',
      })
    },
    data () {
      return {
        formName: 'priorKnowledge',
        previousSalesConsultant: '',
        salesTraining: '',
        packageTravelRegulations: '',
        passportAndVisaExperience: '',
        FCDOAdviceExperience: '',
        localClimateExperience: '',
        californiaKnowledge: '',
        floridaKnowledge: '',
        westCanadaKnowledge: '',
        eastCoastAustraliaKnowledge: '',
        newZealandKnowledge: '',
        southAfricaKnowledge: '',
        thailandKnowledge: '',
        italyKnowledge: '',
        spainKnowledge: '',
        vietnamKnowledge: '',
        flyDriveKnowledge: '',
        luxuryBeachKnowledge: '',
        familyKKnowledge: '',
        bespokePrivateKnowledge: '',
        escortedCoachingKnowledge: '',
        adventureGroupKnowledge: '',
        cruiseKnowledge: '',
        GAPYearKnowledge: '',
        businessClassFlightsKnowledge: '',
        railKnowledge: '',
        skiKnowledge: ''
      }
    },
    mounted () {
      setTimeout(() => {
        this.previousSalesConsultant = this.form.previousSalesConsultant
        this.salesTraining = this.form.salesTraining
        this.packageTravelRegulations = this.form.packageTravelRegulations
        this.passportAndVisaExperience = this.form.passportAndVisaExperience
        this.FCDOAdviceExperience = this.form.FCDOAdviceExperience
        this.localClimateExperience = this.form.localClimateExperience
        this.californiaKnowledge = this.form.californiaKnowledge
        this.floridaKnowledge = this.form.floridaKnowledge
        this.westCanadaKnowledge = this.form.westCanadaKnowledge
        this.eastCoastAustraliaKnowledge = this.form.eastCoastAustraliaKnowledge
        this.newZealandKnowledge = this.form.newZealandKnowledge
        this.southAfricaKnowledge = this.form.southAfricaKnowledge
        this.thailandKnowledge = this.form.thailandKnowledge
        this.italyKnowledge = this.form.italyKnowledge
        this.spainKnowledge = this.form.spainKnowledge
        this.vietnamKnowledge = this.form.vietnamKnowledge
        this.flyDriveKnowledge = this.form.flyDriveKnowledge
        this.luxuryBeachKnowledge = this.form.luxuryBeachKnowledge
        this.familyKKnowledge = this.form.familyKKnowledge
        this.bespokePrivateKnowledge = this.form.bespokePrivateKnowledge
        this.escortedCoachingKnowledge = this.form.escortedCoachingKnowledge
        this.adventureGroupKnowledge = this.form.adventureGroupKnowledge
        this.cruiseKnowledge = this.form.cruiseKnowledge
        this.GAPYearKnowledge = this.form.GAPYearKnowledge
        this.businessClassFlightsKnowledge = this.form.businessClassFlightsKnowledge
        this.railKnowledge = this.form.railKnowledge
        this.skiKnowledge = this.form.skiKnowledge
      }, 100)
      this.$v.$touch()
    },
    updated () {
      // this.updateForm('valid', this.$v.$error)
      const validationData = {
        section: 'priorKnowledge',
        status: !this.$v.form.$invalid
      }
      this.updateOnboardingCompleteAction(validationData)
    },
    methods: {
      ...mapActions({
        updateOnboardingFormAction: 'authStore/updateOnboardingFormAction',
        currentOnboardingFormAction: 'authStore/currentOnboardingFormAction',
        setRecruitAction: 'authStore/setRecruitAction',
        updateOnboardingCompleteAction: 'authStore/updateOnboardingCompleteAction'
      }),
      handleInput(input, e, nested, array, index, item) {
        this.updateForm(input, e, nested, array, index, item)
      },
      handleFileSelect (e, type) {
        if (e.name && type === 'english') {
          this.displayEnglishUploadBtn = true
        }
        if (e.name && type === 'maths') {
          this.displayMathsUploadBtn = true
        }
      },
      async handleFileUpload (type) {
        const formData = new FormData()
        let res
        if (type === 'english') {
          formData.append('englishFile', this.form.englishFileUpload)
          res = await uploadEnglishCertificate(this.getToken, formData)
          if (res && res.data && res.data.success) {
            this.displayEnglishUploadBtn = false
          }
        }
        if (type === 'maths') {
          formData.append('mathsFile', this.form.mathsFileUpload)
          res = await uploadMathsCertificate(this.getToken, formData)
          if (res && res.data && res.data.success) {
            this.displayMathsUploadBtn = false
          }
        }
      },
      updateForm
    },
    validations: {
      form: {
        previousSalesConsultant: {
          required: required
        },
        agencyName: {
          required: requiredIf(function() {
            if (this.previousSalesConsultant === 'yes') {
              return true
            }
          })
        },
        howLong: {
          required: requiredIf(function() {
            if (this.previousSalesConsultant === 'yes') {
              return true
            }
          })
        },
        travelQualifications: {
          required: required
        },
        salesTraining: {
          required: required
        },
        salesTrainingDetails: {
          required: requiredIf(function() {
            if (this.salesTraining === 'yes') {
              return true
            }
          })
        },
        packageTravelRegulations: {
          required: required
        },
        flightGDS: {
          required: required
        },
        passportAndVisaExperience: {
          required: required
        },
        FCDOAdviceExperience: {
          required: required
        },
        localClimateExperience: {
          required: required
        },
        californiaKnowledge: {
          required: required
        },
        floridaKnowledge: {
          required: required
        },
        westCanadaKnowledge: {
          required: required
        },
        eastCoastAustraliaKnowledge: {
          required: required
        },
        newZealandKnowledge: {
          required: required
        },
        southAfricaKnowledge: {
          required: required
        },
        thailandKnowledge: {
          required: required
        },
        italyKnowledge: {
          required: required
        },
        spainKnowledge: {
          required: required
        },
        vietnamKnowledge: {
          required: required
        },
        destinationKnowledge: {
          required: required
        },
        flyDriveKnowledge: {
          required: required
        },
        luxuryBeachKnowledge: {
          required: required
        },
        familyKKnowledge: {
          required: required
        },
        bespokePrivateKnowledge: {
          required: required
        },
        escortedCoachingKnowledge: {
          required: required
        },
        adventureGroupKnowledge: {
          required: required
        },
        cruiseKnowledge: {
          required: required
        },
        GAPYearKnowledge: {
          required: required
        },
        businessClassFlightsKnowledge: {
          required: required
        },
        railKnowledge: {
          required: required
        },
        skiKnowledge: {
          required: required
        }
      }
    }
  }
  </script>
  
  <style scoped>
  .question-group {
    width: 100%;
    margin: 2rem 0;
  }
  .q-a {
    display: grid;
    grid-template-columns: 10% 90%;
  }
  </style>
  